import { useGraphqlQuery } from "graphql-mock"
import { useQueryParam } from "hooks"
import { IconCertified, ProgressBar, useWindowWidth } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { toPercent } from "utils"
import { Stars } from "../../Stars"
import { UserFilter } from "../UserWidget/UserFilters"
import { query } from "./gql"
import { UserInfoHeaderError } from "./UserInfoHeaderError"
import { UserInfoHeaderSkeleton } from "./UserInfoHeaderSkeleton"

import s from "./UserInfoHeader.module.scss"

export const UserInfoHeader = () => {
  const { isBigWindow } = useWindowWidth()
  const { data, error, isPending } = useGraphqlQuery(
    query,
    {},
    { enabled: !isBigWindow }
  )
  const { t } = useTranslation()

  const [incompleteQueryParam, setIncompleteQueryParam] =
    useQueryParam("incomplete")

  const [mandatoryQueryParam, setMandatoryQueryParam] =
    useQueryParam("mandatory")

  const isMandatory = mandatoryQueryParam === "true"
  const isIncomplete = incompleteQueryParam === "true"

  if (isBigWindow) return
  if (isPending) return <UserInfoHeaderSkeleton />
  if (error) return <UserInfoHeaderError error={error} />

  const {
    scenarioCompleteCount,
    scenarioCompleteMax,

    scenarioMandatoryCompleteCount,
    scenarioMandatoryCompleteMax,

    productCompleteMax,
    productCompleteCount,

    productStarsCount,
    productStarsMax,

    productMandatoryCompleteCount,
    productMandatoryCompleteMax,

    productMandatoryStarsCount,
    productMandatoryStarsMax,
  } = data.userProgressSummary

  const incompleteCount = productCompleteMax - productCompleteCount

  const value = isMandatory ? productMandatoryStarsCount : productStarsCount
  const maxValue = isMandatory ? productMandatoryStarsMax : productStarsMax

  return (
    <div className={s.header}>
      <div className={s.row}>
        <div className={s.stars}>
          <Stars
            count={1}
            max={1}
            size="extraSmall"
            ariaLabel={`${productStarsCount} of ${productStarsMax}`}
          />
          <div className={s.stars__value}>
            {value}/{maxValue}
          </div>
        </div>
        <Completed
          label={t("user.COMPLETED")}
          value={
            isMandatory ? scenarioMandatoryCompleteCount : scenarioCompleteCount
          }
          maxValue={
            isMandatory ? scenarioMandatoryCompleteMax : scenarioCompleteMax
          }
          isActive={false}
        />
        <UserFilter
          onClick={() => {
            setMandatoryQueryParam(isMandatory ? undefined : "true")
          }}
          isActive={isMandatory}
          text={t("user.MANDATORY")}
          progress={`${productMandatoryCompleteCount}/${productMandatoryCompleteMax}`}
          variant="mandatory"
          isLoading={isPending}
        />
        <UserFilter
          onClick={() => {
            setIncompleteQueryParam(isIncomplete ? undefined : "true")
          }}
          isActive={isIncomplete}
          text={t("user.INCOMPLETE")}
          progress={`${incompleteCount}`}
          variant="incomplete"
          isLoading={isPending}
        />
      </div>
      <Progress
        value={
          isMandatory ? scenarioMandatoryCompleteCount : scenarioCompleteCount
        }
        maxValue={
          isMandatory ? scenarioMandatoryCompleteMax : scenarioCompleteMax
        }
        variant={isMandatory ? "mandatory" : "completed"}
      />
    </div>
  )
}

const Completed = ({
  value,
  maxValue,
  label,
  isActive,
}: {
  value: number | string
  maxValue: number | string
  label: string
  isActive: boolean
}) => {
  return (
    <div className={modifiers(s, "completed", { isActive })}>
      <IconCertified />
      <div className={s.completed__value}>
        {toPercent(Number(value), Number(maxValue))}%
      </div>
      <div className={s.completed__label}>{label}</div>
    </div>
  )
}

const Progress = ({
  value,
  maxValue,
  variant,
}: {
  value: number
  maxValue: number
  variant: "mandatory" | "completed"
}) => (
  <div className={s.progress}>
    <ProgressBar
      value={value}
      maxValue={maxValue}
      color={variant === "completed" ? "green-30" : "purple-40"}
      backgroundColor={variant === "completed" ? "green-10" : "marble"}
    />
  </div>
)
